import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { usePatientConsultations } from '@/hooks/usePatientConsultations'
import { VITALS, toNumericVital } from '@/lib/vitals'

interface VitalsTrendChartProps {
  patientId: string
  /** Open the note a data point came from in read-only mode (REQ-FE-03). */
  onOpenNote?: (consultationId: string) => void
}

interface Point {
  date: string
  value: number
  consultationId: string
}

function Sparkline({ points, onOpenNote }: { points: Point[]; onOpenNote?: (id: string) => void }) {
  if (points.length < 2) return null
  const values = points.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const w = 72
  const h = 22
  const step = w / (points.length - 1)
  const coords = points.map((p, i) => ({
    x: i * step,
    y: h - ((p.value - min) / range) * h,
    p,
  }))
  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')
  return (
    <svg width={w} height={h + 4} className="shrink-0 text-ml-primary overflow-visible">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" transform="translate(0,2)" />
      {coords.map((c, i) => (
        <circle
          key={i}
          cx={c.x}
          cy={c.y + 2}
          r={onOpenNote ? 3 : 2}
          fill="currentColor"
          className={onOpenNote ? 'cursor-pointer hover:opacity-70' : ''}
          onClick={onOpenNote ? () => onOpenNote(c.p.consultationId) : undefined}
        >
          <title>
            {new Date(c.p.date).toLocaleDateString('es-MX')}: {c.p.value}
            {onOpenNote ? ' — abrir nota' : ''}
          </title>
        </circle>
      ))}
    </svg>
  )
}

/**
 * NOM-004 REQ-EVO-02 / REQ-FE-02 — trend of structured vitals across ALL
 * signed notes. Reads the single per-patient source (usePatientConsultations);
 * deltas are computed on read and nothing is persisted (REQ-FE-05). Every
 * metric in the shared vitals registry that has data is trended, missing
 * values are skipped (gaps don't break the trend), and each point links back
 * to its source note (REQ-FE-03).
 */
export function VitalsTrendChart({ patientId, onOpenNote }: VitalsTrendChartProps) {
  const { signed, isLoading } = usePatientConsultations(patientId)

  if (isLoading) {
    return (
      <div className="space-y-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-9 rounded border border-ml-outline-variant/15 bg-ml-surface-container-lowest animate-pulse" />
        ))}
      </div>
    )
  }

  // Oldest → newest so deltas read in clinical order.
  const ordered = [...signed].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  )

  const series = VITALS.map((metric) => {
    const points: Point[] = ordered
      .map((c) => {
        const value = toNumericVital(c.vitals?.[metric.key])
        return value != null ? { date: c.created_at, value, consultationId: c.id } : null
      })
      .filter((p): p is Point => p !== null)
    return { ...metric, points }
  }).filter((s) => s.points.length > 0)

  if (series.length === 0) {
    return (
      <p className="text-xs text-ml-on-surface-variant/60 italic">
        Aún no hay signos vitales registrados para graficar tendencias.
      </p>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-ml-outline-variant/15">
      {series.map((s, idx) => {
        const last = s.points[s.points.length - 1]
        const prev = s.points.length > 1 ? s.points[s.points.length - 2] : null
        const delta = prev ? last.value - prev.value : 0
        const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus
        const dir = delta === 0 ? 0 : delta > 0 ? 1 : -1
        const adverse = s.higherIsWorse == null ? null : dir === (s.higherIsWorse ? 1 : -1)
        const deltaColor =
          dir === 0 || adverse == null
            ? 'text-ml-on-surface-variant/50'
            : adverse
              ? 'text-red-600'
              : 'text-teal-600'
        return (
          <div
            key={s.key}
            className={
              'flex items-center gap-3 px-3 py-2 ' +
              (idx > 0 ? 'border-t border-ml-outline-variant/10 ' : '') +
              'bg-ml-surface'
            }
          >
            {/* Label */}
            <p className="w-28 shrink-0 truncate text-[10px] font-bold uppercase tracking-wider text-ml-on-surface-variant/70">
              {s.label}
            </p>

            {/* Current value */}
            <p className="shrink-0 font-headline text-base italic text-ml-primary leading-none">
              {last.value}
              <span className="ml-0.5 text-[9px] not-italic text-ml-on-surface-variant/60">{s.unit}</span>
            </p>

            {/* Delta */}
            <span className={`flex shrink-0 items-center gap-0.5 text-[10px] font-medium ${deltaColor}`}>
              {prev ? (
                <>
                  <DeltaIcon className="h-3 w-3" />
                  {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                </>
              ) : (
                <span className="text-ml-on-surface-variant/30">—</span>
              )}
            </span>

            {/* Sparkline — hidden on very small containers */}
            <span className="ml-auto hidden sm:block">
              <Sparkline points={s.points} onOpenNote={onOpenNote} />
            </span>
          </div>
        )
      })}
    </div>
  )
}
